import React, { useMemo, useEffect } from "react";
import Grid from "@mui/material/Grid";
import {
  IoFlaskOutline,
  IoPeopleOutline,
  IoAlertCircleOutline,
  IoCheckmarkCircleOutline,
  IoTimeOutline,
  IoTrophyOutline,
} from "react-icons/io5";

import VuiBox from "components/ui/VuiBox";
import StandardMessageDisplay from "components/StandardMessageDisplay";
import MiniStatCard, { MiniStatCardSkeleton } from "components/MiniStatCard";

// Layout
import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";

// Context
import { useAnalytics } from "context/AnalyticsContext";
import { analyticsService } from "services/trackingService";

// Data hook
import { useRDLandscapeData } from "./data/useRDLandscapeData";

// Page components
import TimelineRadar from "./components/TimelineRadar";
import ThreatMatrix from "./components/ThreatMatrix";
import TrialIntelligenceCards from "./components/TrialIntelligenceCards";
import TrialsTable from "./components/TrialsTable";
import KOLCards from "./components/KOLCards";

// ---------------------------------------------------------------------------
// Threat-level parsing from competitive_implications free text
// ---------------------------------------------------------------------------
const THREAT_RANK = { high: 3, medium: 2, moderate: 2, low: 1 };

function parseThreatLevel(text) {
  const lower = (text || "").toLowerCase();
  let maxRank = 0;
  let label = null;
  Object.entries(THREAT_RANK).forEach(([word, rank]) => {
    if (lower.includes(word) && rank > maxRank) {
      maxRank = rank;
      label = word === "moderate" ? "Medium" : word.charAt(0).toUpperCase() + word.slice(1);
    }
  });
  return label || "N/A";
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------
function RDLandscapePage() {
  const { rdCompetitorId } = useAnalytics();
  const { trials, allTrials, loading, error } = useRDLandscapeData();
  const isMarketView = rdCompetitorId === "market";

  useEffect(() => {
    analyticsService.trackPageView("RD_LANDSCAPE", {
      competitorId: rdCompetitorId,
    });
  }, [rdCompetitorId]);

  // ── KPI computations (from the unfiltered pool) ───────────────────────────
  const stats = useMemo(() => {
    if (!allTrials || allTrials.length === 0) return null;

    const totalTrials     = allTrials.length;
    const avgEnrollment   = Math.round(
      allTrials.reduce((s, t) => s + (t.enrollment || 0), 0) / totalTrials
    );
    const activeTrials    = allTrials.filter((t) =>
      ["RECRUITING", "NOT_YET_RECRUITING", "ACTIVE_NOT_RECRUITING"].includes(t.status)
    ).length;
    const completedTrials = allTrials.filter((t) => t.status === "COMPLETED").length;

    // Highest threat level across all trials (from competitive_implications text)
    let maxThreatRank = 0;
    let threatLabel = "N/A";
    allTrials.forEach((t) => {
      const text  = (t.ai_analysis?.competitive_implications || "").toLowerCase();
      const label = parseThreatLevel(text);
      const rank  = THREAT_RANK[label?.toLowerCase()] || 0;
      if (rank > maxThreatRank) { maxThreatRank = rank; threatLabel = label; }
    });

    // Leading R&D sponsor (market view only) — sponsor with most trials
    const sponsorCounts = {};
    allTrials.forEach((t) => {
      if (t.sponsor) sponsorCounts[t.sponsor] = (sponsorCounts[t.sponsor] || 0) + 1;
    });
    const leadingSponsor =
      Object.entries(sponsorCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A";

    return { totalTrials, avgEnrollment, activeTrials, completedTrials, threatLabel, leadingSponsor };
  }, [allTrials]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <VuiBox py={3}>
        {/* ── KPI Strip ─────────────────────────────────────────────────── */}
        <Grid container spacing={2} mb={3}>
          {/* 1 — Total Trials */}
          <Grid item xs={6} sm={4} lg>
            {loading ? <MiniStatCardSkeleton /> : (
              <MiniStatCard
                title={{ fontWeight: "medium", text: "Total Trials" }}
                count={stats ? stats.totalTrials.toLocaleString() : "—"}
                icon={{ component: <IoFlaskOutline size="22px" color="white" /> }}
              />
            )}
          </Grid>

          {/* 2 — Avg Enrollment */}
          <Grid item xs={6} sm={4} lg>
            {loading ? <MiniStatCardSkeleton /> : (
              <MiniStatCard
                title={{ fontWeight: "medium", text: "Avg Enrollment" }}
                count={stats ? stats.avgEnrollment.toLocaleString() : "—"}
                icon={{ component: <IoPeopleOutline size="22px" color="white" /> }}
              />
            )}
          </Grid>

          {/* 3 — Threat Level (single competitor) OR Leading R&D (market) */}
          <Grid item xs={6} sm={4} lg>
            {loading ? <MiniStatCardSkeleton /> : isMarketView ? (
              <MiniStatCard
                title={{ fontWeight: "medium", text: "Leading R&D" }}
                count={stats?.leadingSponsor || "—"}
                icon={{ component: <IoTrophyOutline size="22px" color="white" /> }}
              />
            ) : (
              <MiniStatCard
                title={{ fontWeight: "medium", text: "Threat Level" }}
                count={stats?.threatLabel || "—"}
                icon={{ component: <IoAlertCircleOutline size="22px" color="white" /> }}
              />
            )}
          </Grid>

          {/* 4 — Active Trials */}
          <Grid item xs={6} sm={4} lg>
            {loading ? <MiniStatCardSkeleton /> : (
              <MiniStatCard
                title={{ fontWeight: "medium", text: "Active Trials" }}
                count={stats ? stats.activeTrials.toLocaleString() : "—"}
                icon={{ component: <IoTimeOutline size="22px" color="white" /> }}
              />
            )}
          </Grid>

          {/* 5 — Completed Trials */}
          <Grid item xs={6} sm={4} lg>
            {loading ? <MiniStatCardSkeleton /> : (
              <MiniStatCard
                title={{ fontWeight: "medium", text: "Completed Trials" }}
                count={stats ? stats.completedTrials.toLocaleString() : "—"}
                icon={{ component: <IoCheckmarkCircleOutline size="22px" color="white" /> }}
              />
            )}
          </Grid>
        </Grid>

        {/* ── Error state ───────────────────────────────────────────────────── */}
        {!loading && error && (
          <StandardMessageDisplay
            type="error"
            title="Error Loading Clinical Trials"
            message={error}
            containerHeight="400px"
          />
        )}

        {/* ── Content sections ─────────────────────────────────────────────── */}
        {!error && (
          <>
            {/* Tier 1: Timeline Radar — handles its own loading/empty state */}
            <VuiBox mb={3}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TimelineRadar trials={trials} loading={loading} />
                </Grid>
              </Grid>
            </VuiBox>

            {/* Tier 2: Threat Matrix — handles its own loading/empty state */}
            <VuiBox mb={3}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <ThreatMatrix trials={trials} loading={loading} />
                </Grid>
              </Grid>
            </VuiBox>

            {/* Tier 3: Trial Intelligence Cards — handles its own loading/empty state */}
            <VuiBox mb={3}>
              <TrialIntelligenceCards trials={trials} loading={loading} />
            </VuiBox>

            {/* Tier 4: Trials Data Table — handles its own loading/empty state */}
            <VuiBox mb={3}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TrialsTable trials={trials} loading={loading} />
                </Grid>
              </Grid>
            </VuiBox>

            {/* Tier 5: Key Opinion Leaders — only when data loaded and not market view */}
            {!loading && !isMarketView && allTrials.length > 0 && (
              <VuiBox mb={3}>
                <KOLCards trials={trials} />
              </VuiBox>
            )}
          </>
        )}
      </VuiBox>
    </DashboardLayout>
  );
}

export default RDLandscapePage;
